import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  Avatar,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material"
import LoginIcon from "@mui/icons-material/Login"
import LogoutIcon from "@mui/icons-material/Logout"
import { fetchMe, logout, loginUrl } from "../utils/authApi"

const AuthContext = createContext({ user: null, loading: true, signOut: () => {} })

export const useAuth = () => useContext(AuthContext)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetchMe().then((u) => {
      if (!cancelled) {
        setUser(u)
        setLoading(false)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  const signOut = useCallback(async () => {
    await logout().catch(() => {})
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * App-bar account control: a "Sign in" button when logged out, the user's
 * avatar with a small menu (email + sign out) when logged in. Renders
 * nothing while the initial /auth/me check is in flight to avoid a
 * sign-in flash for logged-in users.
 */
export function AccountButton() {
  const { user, loading, signOut } = useAuth()
  const [anchorEl, setAnchorEl] = useState(null)

  if (loading) return null

  if (!user) {
    return (
      <Button
        href={loginUrl()}
        startIcon={<LoginIcon />}
        sx={{
          color: "text.secondary",
          fontSize: "0.85rem",
          borderRadius: 2,
          px: 1.5,
          whiteSpace: "nowrap",
        }}
      >
        Sign in
      </Button>
    )
  }

  const initial = (user.display_name || user.email || "?")[0].toUpperCase()

  return (
    <>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
        <Avatar
          src={user.avatar_url || undefined}
          alt={user.display_name || user.email}
          sx={{ width: 30, height: 30, fontSize: "0.9rem" }}
        >
          {initial}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {user.display_name || user.email}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            signOut()
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  )
}
